<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useConfirm } from 'primevue/useconfirm'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/dropdown-menu'
import { isBetaOptIn, withBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'
import AdminConfirmDialog from './AdminConfirmDialog.vue'

const CONFIRM_GROUP = 'draft-actions'

const props = defineProps<{ uuid: string }>()

const emit = defineEmits<{
  'save-draft': []
  'discard-draft': []
}>()

const route = useRoute()
const confirm = useConfirm()
const menuTrigger = ref<InstanceType<typeof DropdownMenuTrigger> | null>(null)

// The preview opens in a new tab, which only reaches the Vue 3 anchor page if it carries the opt-in.
const previewUrl = computed(() => {
  const url = `${toolkitUrls.anchorPage(props.uuid)}?draft=true`
  return isBetaOptIn(route.query) ? withBetaOptIn(url) : url
})

// The dialog opened from a menu item that no longer exists, so it can't return focus there itself.
async function returnFocusToMenu() {
  await nextTick()
  menuTrigger.value?.$el?.focus()
}

function confirmThen(header: string, message: string, onAccept: () => void) {
  confirm.require({
    group: CONFIRM_GROUP,
    header,
    message,
    icon: 'warning_amber',
    accept: () => {
      onAccept()
      returnFocusToMenu()
    },
    reject: returnFocusToMenu,
    onHide: returnFocusToMenu
  })
}

const discardDraft = () => confirmThen(
  'Discard Changes',
  'Are you sure you want to discard unpublished changes? They will be permanently deleted.',
  () => emit('discard-draft')
)

const saveDraft = () => confirmThen(
  'Publish Changes',
  'Are you sure you want to save unpublished changes? They will become public.',
  () => emit('save-draft')
)
</script>

<template>
  <div class="inline-flex">
    <a
      :href="previewUrl"
      target="_blank"
      class="inline-flex items-center h-10 px-4 text-md text-white bg-purple-400 rounded-l-xs hover:bg-purple-500 focus-visible:outline-primary-400"
    >Preview Changes</a>
    <!-- non-modal: a modal menu marks the rest of the page aria-hidden while open (axe aria-hidden-focus) -->
    <DropdownMenu :modal="false">
      <DropdownMenuTrigger
        ref="menuTrigger"
        aria-label="Open draft page options"
        class="inline-flex items-center h-10 px-2 text-white bg-purple-400 border-l border-purple-500 rounded-r-xs hover:bg-purple-500 data-[state=open]:bg-purple-300 focus-visible:outline-primary-400"
      >
        <span class="material-icons-round" aria-hidden="true">arrow_drop_down</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="bg-white">
        <DropdownMenuItem class="cursor-pointer hover:bg-gray-300" @select="discardDraft">Discard Changes</DropdownMenuItem>
        <DropdownMenuItem class="cursor-pointer hover:bg-gray-300" @select="saveDraft">Publish Changes</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <AdminConfirmDialog :group="CONFIRM_GROUP" />
  </div>
</template>
